import cx from 'classnames'
import { Fragment, useMemo, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import useOpenConferenceCall from 'features/ConferenceCall/hooks/useConferenceCall'
import { useCurrentUser } from 'store/api/yay/user'
import { addNotification } from 'store/slices/notifications'
import { useDetectClickOutside, useResponsive } from 'hooks'

import ConfirmBoxBasic from 'components/ConfirmBoxBasic'

const ChatRoomHeader = ({
  chatRoom,
  onClose,
  onMenuClicked,
  disableCallFeature,
  onSendMessage,
  isExchanged,
}: {
  chatRoom: {
    isSuccess: boolean
    data?: any
  }
  onClose: any
  onMenuClicked: any
  disableCallFeature: boolean
  onSendMessage: Function
  isExchanged: boolean
}) => {
  const { isMobile } = useResponsive()
  const { t } = useTranslation()
  const { openConferenceCall } = useOpenConferenceCall()
  const dispatch = useDispatch()

  const [showKentaWarning, setShowKentaWarning] = useState(false)
  const [showCallOption, setShowCallOption] = useState(false)

  const { data: currentUser } = useCurrentUser()
  const clickOutsideRef = useRef(null)

  useDetectClickOutside(
    clickOutsideRef,
    () => setShowCallOption(false),
    showCallOption
  )

  const createCallMessage = async (type = null) => {
    const isGroupChat = chatRoom?.data?.is_group ?? false

    const res = await onSendMessage({
      messageType: isGroupChat ? 'call' : 'individual_call',
      call_type: type,
    })

    if (res?.['data']?.result == 'success') {
      openConferenceCall({
        conferenceCallId: res?.['data']?.conference_call?.id,
        allowOnMobile: true,
        inviteId: !isGroupChat ? chatRoom?.data?.id : undefined,
        targetId: chatRoom?.data?.id,
      })
    }

    setShowCallOption(false)
  }

  const createVoiceCall = async () => {
    await createCallMessage()
  }

  const createVideoCall = async () => {
    await createCallMessage('vdo')
  }

  const onToggleCallOption = useCallback(() => {
    if (isExchanged) {
      setShowCallOption(!showCallOption)
    } else {
      dispatch(
        addNotification({
          message: t('chatroom_header.call_menu.call_limit_dialog_message'),
          variant: 'snackbar',
          type: 'error',
        })
      )
    }
  }, [showCallOption, isExchanged])

  const chatRoomTitle = useMemo(() => {
    if (!chatRoom.isSuccess) {
      return
    }

    if (chatRoom?.data?.is_group ?? false) {
      let maxLength = 35
      let memberList = (chatRoom?.data?.members ?? [])
        .map((member: any) => member?.nickname)
        .join(', ')

      if (isMobile) {
        maxLength = 17
      }

      if (memberList.length > maxLength) {
        memberList =
          memberList.substring(0, maxLength / 2) +
          '...' +
          memberList.substring(memberList.length - maxLength / 2)
      }

      return `${memberList}(${(chatRoom?.data?.members ?? []).length})`
    }

    return (chatRoom?.data?.members ?? []).filter(
      ({ id }) => id !== currentUser?.id
    )?.[0]?.nickname
  }, [chatRoom, isMobile])

  const opponentUser = useMemo(
    () =>
      (chatRoom?.data?.members ?? []).find(
        (member: any) => member.id !== currentUser?.id
      ),
    [chatRoom, currentUser]
  )

  return (
    <Fragment>
      <div className="ChatRoomHeader" ref={clickOutsideRef}>
        <div className="ChatRoomHeader__bar">
          <a
            className="ChatRoomHeader__bar__close"
            role="button"
            onClick={onClose}
          />

          {chatRoomTitle && (
            <div className="ChatRoomHeader__bar__title_wrapper">
              <div className="ChatRoomHeader__bar__title">{chatRoomTitle}</div>

              {(opponentUser?.recently_kenta ?? false) && (
                <a role="button" onClick={() => setShowKentaWarning(true)}>
                  <img
                    src="/images/icon-caution.svg"
                    width="18px"
                    height="18px"
                    alt="icon-caution"
                  />
                </a>
              )}
            </div>
          )}
          <div
            className={cx(
              'ChatRoomHeader__bar__call',
              disableCallFeature && 'ChatRoomHeader__bar__call--disable'
            )}
            onClick={disableCallFeature ? () => {} : onToggleCallOption}
            role="button"
          />

          <div
            className="ChatRoomHeader__bar__handle"
            role="button"
            onClick={onMenuClicked}
          />
        </div>

        {showCallOption && (
          <div className="ChatRoomHeader__call_menu">
            <div
              className="ChatRoomHeader__call_menu__voice"
              onClick={createVoiceCall}
              role="button"
            >
              {t('chatroom_header.call_menu.voice')}
            </div>

            <div
              className="ChatRoomHeader__call_menu__video"
              onClick={createVideoCall}
              role="button"
            >
              {t('chatroom_header.call_menu.video')}
            </div>
          </div>
        )}
      </div>

      <ConfirmBoxBasic
        isActive={showKentaWarning}
        title={t('chatroom_header.kenta.title')}
        content={t('chatroom_header.kenta.content')}
        confirmButtonText={t('chatroom_header.kenta.confirm_button')}
        onClose={() => setShowKentaWarning(false)}
        onOk={() => setShowKentaWarning(false)}
      />
    </Fragment>
  )
}

export default ChatRoomHeader
